import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Save, ChevronUp, ChevronDown, Trash2 } from 'lucide-react';
import api from '../api/client';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../hooks/useAuth';

type Term = { so: string; en: string };
type TermsData = { terms: Term[]; authorisation_text: string };

const MAX_TERMS = 15;
const EDIT_PERM = 'settings.repair_terms.edit';

const RepairTermsTab: React.FC = () => {
  const { showToast } = useToast();
  const { hasPermission, isSuperAdmin } = useAuth();
  const canEdit = isSuperAdmin || hasPermission(EDIT_PERM);

  const [form, setForm] = useState<TermsData | null>(null);
  const [saved, setSaved] = useState<TermsData | null>(null);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/settings/repair-terms')
      .then(r => { setForm(r.data.data); setSaved(r.data.data); })
      .catch(() => showToast('Could not load the repair terms', 'error'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dirty = useMemo(() => !!form && !!saved && JSON.stringify(form) !== JSON.stringify(saved), [form, saved]);

  if (loading) return <div className="card"><div className="card-body" style={{ fontSize: 12, color: 'var(--tx3)' }}>Loading...</div></div>;
  if (!form) return null;

  const setTerm = (i: number, lng: 'so' | 'en', v: string) =>
    setForm(f => f && ({ ...f, terms: f.terms.map((t, idx) => idx === i ? { ...t, [lng]: v } : t) }));

  const addTerm = () =>
    setForm(f => f && f.terms.length < MAX_TERMS ? { ...f, terms: [...f.terms, { so: '', en: '' }] } : f);

  const removeTerm = (i: number) => {
    if (form.terms.length <= 1) { showToast('Keep at least one term', 'error'); return; }
    setForm(f => f && ({ ...f, terms: f.terms.filter((_, idx) => idx !== i) }));
  };

  const moveTerm = (i: number, dir: -1 | 1) =>
    setForm(f => {
      if (!f) return f;
      const j = i + dir;
      if (j < 0 || j >= f.terms.length) return f;
      const terms = [...f.terms];
      [terms[i], terms[j]] = [terms[j], terms[i]];
      return { ...f, terms };
    });

  const save = async () => {
    setSaving(true); setErrors({});
    try {
      const r = await api.put('/settings/repair-terms', form);
      setForm(r.data.data); setSaved(r.data.data);
      showToast(r.data.message || 'Terms saved');
    } catch (err: any) {
      if (err.response?.status === 422) { setErrors(err.response.data.errors || {}); showToast('Fix the highlighted fields', 'error'); }
      else showToast(err.response?.data?.message || 'Could not save', 'error');
    } finally { setSaving(false); }
  };

  const err = (k: string) => errors[k]?.[0];
  const box = (k: string): React.CSSProperties => (err(k) ? { borderColor: 'var(--red)' } : {});

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 16, alignItems: 'start' }}>
      {/* EDITOR */}
      <div className="card">
        <div className="card-header">
          <span>Repair Ticket Terms {dirty && <span className="badge badge-amber" style={{ marginLeft: 6 }}>Unsaved</span>}</span>
          <span className="mono" style={{ fontSize: 11, color: 'var(--tx3)' }}>{form.terms.length}/{MAX_TERMS}</span>
        </div>
        <div className="card-body">
          {!canEdit && <div style={{ fontSize: 12, color: 'var(--tx3)', marginBottom: 12 }}>View only - you do not have permission to edit the terms.</div>}

          {form.terms.map((t, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '10px 0', borderBottom: '1px solid var(--bdr)' }}>
              <div className="mono" style={{ width: 22, paddingTop: 26, fontSize: 12, fontWeight: 700, color: 'var(--blue)' }}>{i + 1}</div>
              <div className="form-grid-2" style={{ flex: 1, minWidth: 0, marginBottom: 0 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label" htmlFor={`so-${i}`}>Somali</label>
                  <textarea id={`so-${i}`} className="form-control" rows={2} disabled={!canEdit} value={t.so}
                    style={box(`terms.${i}.so`)} onChange={e => setTerm(i, 'so', e.target.value)} />
                  {err(`terms.${i}.so`) && <div style={{ fontSize: 11, color: 'var(--red)' }}>{err(`terms.${i}.so`)}</div>}
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label" htmlFor={`en-${i}`}>English</label>
                  <textarea id={`en-${i}`} className="form-control" rows={2} disabled={!canEdit} value={t.en}
                    style={box(`terms.${i}.en`)} onChange={e => setTerm(i, 'en', e.target.value)} />
                  {err(`terms.${i}.en`) && <div style={{ fontSize: 11, color: 'var(--red)' }}>{err(`terms.${i}.en`)}</div>}
                </div>
              </div>
              {canEdit && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, paddingTop: 22 }}>
                  <button type="button" className="btn btn-secondary btn-sm" aria-label={`Move term ${i + 1} up`} disabled={i === 0} onClick={() => moveTerm(i, -1)}><ChevronUp size={13} /></button>
                  <button type="button" className="btn btn-secondary btn-sm" aria-label={`Move term ${i + 1} down`} disabled={i === form.terms.length - 1} onClick={() => moveTerm(i, 1)}><ChevronDown size={13} /></button>
                  <button type="button" className="btn btn-secondary btn-sm" aria-label={`Delete term ${i + 1}`} style={{ color: 'var(--red)' }} onClick={() => removeTerm(i)}><Trash2 size={13} /></button>
                </div>
              )}
            </div>
          ))}

          {canEdit && (
            <button type="button" className="btn btn-secondary btn-sm" style={{ marginTop: 10 }} disabled={form.terms.length >= MAX_TERMS} onClick={addTerm}>
              <Plus size={13} /> Add term
            </button>
          )}

          <div className="form-group" style={{ marginTop: 18 }}>
            <label className="form-label" htmlFor="auth-text">Customer agreement sentence (printed under the terms)</label>
            <textarea id="auth-text" className="form-control" rows={3} disabled={!canEdit} value={form.authorisation_text}
              style={box('authorisation_text')} onChange={e => setForm(f => f && ({ ...f, authorisation_text: e.target.value }))}
              placeholder="Leave empty to use the ticket's built-in sentence" />
            {err('authorisation_text') && <div style={{ fontSize: 11, color: 'var(--red)' }}>{err('authorisation_text')}</div>}
          </div>

          {canEdit && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
              <button type="button" className="btn btn-secondary" disabled={!dirty || saving} onClick={() => { setForm(saved); setErrors({}); }}>Discard</button>
              <button type="button" className="btn btn-primary" disabled={!dirty || saving} onClick={save}>
                <Save size={13} /> {saving ? '...' : 'Save Terms'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* PREVIEW - same layout as the printed ticket */}
      <div className="card" style={{ position: 'sticky', top: 16 }}>
        <div className="card-header"><span>Ticket preview</span></div>
        <div className="card-body">
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Shuruudaha / Terms and conditions</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 10 }}>
            <tbody>
              {form.terms.map((t, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--bdr)', verticalAlign: 'top' }}>
                  <td style={{ padding: '4px 8px 4px 0', fontSize: 11, width: '50%' }}>{i + 1}. {t.so}</td>
                  <td style={{ padding: '4px 0 4px 8px', fontSize: 11, color: 'var(--tx3)', width: '50%' }}>{t.en}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ fontSize: 12, lineHeight: 1.5, color: form.authorisation_text ? undefined : 'var(--tx3)' }}>
            {form.authorisation_text || '(built-in agreement sentence)'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RepairTermsTab;
